import { CHANGE_TENANT_VALID_STATES } from './constants'

// Constants for all tooltips to ensure consistency across VM components

// Action limitation tooltips
// TTC-FR-003: why a VM action is disabled (single VM only, wrong state, unsupported VM)
export const ACTION_LIMITATION_TOOLTIPS = {
  // Permission-based tooltips
  // TTC-FR-001: actions limited to administrators
  ADMIN_ONLY: 'Only administrators can perform this action',
  MIGRATION_PRIORITY_ADMIN_ONLY: 'Only administrators can set migration priority',

  // General
  SINGLE_VM_ONLY: 'You can only perform this action on a single VM at a time',

  // Server Group
  VM_ALREADY_IN_SERVER_GROUP:
    'This VM is already in a server group. Please select a VM that is not in a server group to add.',
  ADD_SERVER_GROUP_ONE_VM: 'You can only add a single VM to a server group at a time',
  ALL_VMS_NOT_IN_ACTIVE_OR_STOPPED_STATE:
    'All VMs should be in active or stopped state to be removed from a server group.',
  VM_NOT_IN_ACTIVE_OR_STOPPED_STATE:
    'The VM should be in active or stopped state to be added/removed from a server group.',
  VM_NOT_IN_SERVER_GROUP:
    'This VM is not in any server group. Please select a VM that is in a server group to remove.',
  ALL_VMS_NOT_IN_SERVER_GROUP:
    'All VMs should be part of a server group to be removed. Please select VMs that are in a server group to remove.',

  // Lease
  TENANT_VM_LEASE_ONLY: 'You can only customize lease for a tenant VM',
  CUSTOMIZE_LEASE: 'You can only customize lease for a single VM at a time',
  VM_NOT_OWNED: 'VM is not owned by you. Please select a VM owned by you to customize lease.',
  VM_NOT_LEASED: 'VM is not leased. Please select a leased VM to customize lease.',

  // Hotplug
  HOTPLUG_ENABLED: 'Hotplugging is not enabled for this VM.',
  HOTPLUG_ONE_VM: 'You can only hotplug a single VM at a time',
  DISABLED_HOTPLUG_VM_STATE: 'You can only hotplug a VM that are in active or stopped state.',
  RESIZE_HOTPLUG_VM:
    'Resize is not available for hotplug-enabled VMs. Use the Hotplug action instead.',

  // Networking related
  ADD_NETWORK_INTERFACE: 'You can only add a network interface to a single VM at a time',
  REMOVE_NETWORK_INTERFACE: 'You can only remove a network interface from a single VM at a time',

  ADD_PUBLIC_IP: 'You can only add a public IP address to a single VM at a time',
  ADD_PUBLIC_IP_L2_NETWORK: 'You can only add a public IP address to a VM with managed network',
  REMOVE_PUBLIC_IP: 'You can only remove a public IP address from a single VM at a time',
  REMOVE_PUBLIC_IP_L2_NETWORK:
    'You can only remove a public IP address from a VM with managed network',
  EDIT_SECURITY_GROUPS: 'You can only edit security groups for a single VM at a time',
  EDIT_SECURITY_GROUPS_L2_NETWORK:
    'You can only edit security groups for a VM with managed network',
  EDIT_PORT_SECURITY: 'You can only edit port security for a single VM at a time',
  EDIT_PORT_SECURITY_L2_NETWORK: 'You can only edit port security for a VM with managed network',

  // VM management actions
  RENAME_VM: 'You can only rename a single VM at a time',
  CHANGE_OWNER_VM: 'You can only change owner of a single VM at a time',
  // TTC-FR-002: built from CHANGE_TENANT_VALID_STATES so the message follows the allowed states
  CHANGE_TENANT_VM_STATE: `All selected VMs must be in ${CHANGE_TENANT_VALID_STATES.map(
    (s) => s.charAt(0).toUpperCase() + s.slice(1),
  ).join(', ')} state to change tenant`,
  SNAPSHOT_VM: 'You can only create a snapshot for a single VM at a time',
  REVERT_VM_TO_SNAPSHOT: 'You can only revert a single VM at a time',
  REVERT_VM_TO_SNAPSHOT_STATE:
    'The VM must be stopped and have no task in progress to revert it to a snapshot',
  CLONE_VM: 'You can only clone a single VM at a time',
  RESIZE_VM: 'You can only resize a single VM at a time',
  CONFIRM_RESIZE_VM: 'You can only confirm resize for a single VM at a time',
  NOT_RESIZED: 'You can only confirm resize for VMs that are in a resized state',
  ALREADY_RESIZED: 'This VM is already in a resized state',
  EDIT_METADATA: 'You can only edit metadata for a single VM at a time',
  CONSOLE_VM: 'You can only open console for a single VM at a time',
  UPDATE_MIGRATION_PRIORITY: 'You can only update migration priority for a single VM at a time',
  REBUILD_VM: 'You can only rebuild a single VM at a time',
  EDIT_METADATA_VM_RESIZED: 'You cannot Edit Metadata for a VM in resized state.',

  // Volume management
  MANAGE_VOLUMES: 'You can only manage volumes for VMs that are running, suspended or stopped',
  MANAGE_VOLUMES_STATE: 'You can only manage volumes for a running, suspended or stopped VM',
  VOLUME_TERMINATION_SINGLE_VM:
    'You can only configure volume termination for a single VM at a time',
  VOLUME_TERMINATION: 'This VM must have attached volumes to manage volume termination settings',

  // ISO volume attach/detach
  ISO_VOLUME_VM_NOT_STOPPED: 'Stop the VM before attaching or detaching an ISO-based volume',

  // ISO mount/unmount
  MOUNT_ISO_VM_NOT_STOPPED: 'Stop the VM before mounting an ISO',
  MOUNT_ISO_VOLUME_BACKED_REQUIRED:
    'Mount ISO is supported only for VMs that use a volume as the root disk. This VM uses an ephemeral root disk.',
  MOUNT_ISO_ACTIVE_TASK: 'Cannot mount ISO while VM has an active task',
  MOUNT_ISO_LOCKED_VM: 'Cannot mount ISO on a locked VM',
  UNMOUNT_ISO_VM_NOT_STOPPED: 'Stop the VM before unmounting an ISO',
  UNMOUNT_ISO_ACTIVE_TASK: 'Cannot unmount ISO while VM has an active task',
  UNMOUNT_ISO_LOCKED_VM: 'Cannot unmount ISO from a locked VM',
  UNMOUNT_ISO_NO_ISO: 'No ISO is currently mounted on this VM',

  // Power actions
  START: 'You can only start VMs that are in a stopped state',
  STOP: 'You can only stop VMs that are currently running',
  REBOOT: 'You can only reboot VMs that are currently running',
  HARD_REBOOT:
    'You can only hard reboot VMs that are in active, error, hard_reboot, paused, reboot, shutoff, or suspended state',
  SUSPEND: 'You can only suspend VMs that are currently running',
  RESUME: 'You can only resume VMs that are in a suspended state',
  START_ONE_VM: 'You can only start a single VM at a time',
  STOP_ONE_VM: 'You can only stop a single VM at a time',
  PAUSE: 'You can only pause VMs that are currently running',
  UNPAUSE: 'You can only unpause VMs that are in a paused state',
  CANNOT_START_RUNNING: 'You can only start VMs that are in a stopped state',
  CANNOT_STOP_STOPPED: 'You can only stop VMs that are currently running',
  CANNOT_DELETE_ACTIVE_VM: 'You cannot delete an active, paused, or suspended VM',

  // Rescue actions
  RESCUE: 'You can only rescue VMs that are not in a rescued state',
  ALREADY_RESCUED: 'This VM is already in a rescued state',
  UNRESCUE: 'You can only unrescue VMs that are in a rescued state',
  ALREADY_NOT_RESCUED: 'You can only unrescue a rescued VM',

  // Delete on termination
  DELETE_ON_TERMINATION_ENABLE:
    'You can only enable delete on termination for VMs that are active, paused or resized',
  DELETE_ON_TERMINATION_DISABLE:
    'You can only disable delete on termination for VMs that are active, paused or resized',

  // Snapshot limitations
  GPU_PASSTHROUGH_SNAPSHOT: 'Snapshot is not supported for GPU passthrough VMs',

  // Volume state tooltips
  ALREADY_ENABLED: 'Root volume delete on termination is already enabled',
  ALREADY_DISABLED: 'Root volume delete on termination is already disabled',

  // Enable/Disable Root Volume Delete on Termination
  ENABLE_DISABLE_ROOT_VOLUME_DELETE_ON_TERMINATION_SINGLE_VM:
    'You can only enable or disable delete on VM deletion for a single VM at a time',
  ENABLE_DISABLE_ROOT_VOLUME_DELETE_ON_TERMINATION:
    'You can only enable or disable delete on VM deletion for VMs that are active, paused or resized',
}

// Regular tooltip constants (non-disabled state)
// TTC-FR-004: tooltips of enabled actions
export const TOOLTIP_MESSAGES = {
  // Basic action tooltips
  CONSOLE: 'Console',
  DELETE: 'Delete',
  EDIT: 'Edit',
  START: 'Start',
  STOP: 'Stop',

  // Feature-specific tooltips
  GPU_PASSTHROUGH_SNAPSHOT: 'Snapshot is not supported for GPU passthrough VMs',
  SERVER_GROUP_TOOLTIP: 'Server groups allow you to define anti-affinity rules for VMs',

  // Power action descriptions
  REBOOT: 'Reboot the VM',
  HARD_REBOOT: 'Force a hard reboot of the VM',
  SUSPEND: 'Suspend the VM',
  RESUME: 'Resume the VM',

  // Rescue action descriptions
  RESCUE: 'Boot VM in rescue mode',
  UNRESCUE: 'Exit rescue mode',
}

// TTC-FR-005: data volumes need a VM with a volume as its root disk
export const VOLUME_BACKED_VM_ERROR_MSG =
  'You can not attach data volumes to VMs using ephemeral root disk. Create a VM using a volume as root disk before you can attach more data volumes to it.'

export const VM_SNAPSHOT_WARNING_MSG =
  'To attach data volumes, please create the VM using a snapshot of type VM Volume Snapshot.'

export const VOLUME_BACKED_VM_WARNING_MSG =
  'This VM configuration uses ephemeral storage for the root disk. To attach data volumes, create the VM using a volume as the root disk.'

export const DISABLED_VOLUME_BACKED_VM_MSG =
  'To attach a volume, select a VM that uses a volume as the root disk. The currently selected VM uses an ephemeral root disk.'
